
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  RefreshCw,
  Search,
  User,
  Clock,
  Activity,
  Filter,
  Download
} from 'lucide-react';
import { format } from 'date-fns';
import { Json } from '@/integrations/supabase/types';

interface UserActivityLog {
  id: string;
  user_id: string;
  user_email: string;
  activity_type: string;
  activity_details: Json;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const UserActivityLogs = () => {
  const { toast } = useToast();
  const [logs, setLogs] = useState<UserActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  const fetchLogs = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('user_activity_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      if (searchEmail) {
        query = query.ilike('user_email', `%${searchEmail}%`);
      }

      if (filterType) {
        query = query.eq('activity_type', filterType);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Type assertion to handle the database response properly
      const typedData = (data || []).map(log => ({
        ...log,
        ip_address: log.ip_address as string | null,
        user_agent: log.user_agent as string | null,
      })) as UserActivityLog[];

      setLogs(typedData);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load activity logs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage, searchEmail, filterType]);

  const getActivityBadgeColor = (type: string) => {
    switch (type) {
      case 'login':
        return 'bg-green-100 text-green-800';
      case 'logout':
        return 'bg-gray-100 text-gray-800';
      case 'data_export':
        return 'bg-blue-100 text-blue-800';
      case 'mfa_setup':
        return 'bg-purple-100 text-purple-800';
      case 'mfa_verification':
        return 'bg-indigo-100 text-indigo-800';
      case 'password_change':
        return 'bg-orange-100 text-orange-800';
      case 'profile_update':
        return 'bg-yellow-100 text-yellow-800';
      case 'merchant_type_change':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatActivityType = (type: string) => {
    return type.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatActivityDetails = (log: UserActivityLog): string | null => {
    if (!log.activity_details) return null;
    
    const details = log.activity_details as Record<string, unknown>;
    
    if (log.activity_type === 'merchant_type_change') {
      const oldValue = details.old_value as string;
      const newValue = details.new_value as string;
      const orgName = details.organisation_name as string;
      const formatType = (t: string) => t === 'direct' ? 'Direct' : t === 'reseller' ? 'Reseller' : t;
      return `${orgName}: ${formatType(oldValue)} → ${formatType(newValue)}`;
    }
    
    if (log.activity_type === 'login') {
      const method = details.method as string;
      return method ? `via ${method.replace('_', ' ')}` : null;
    }
    
    // PO activity types
    if (log.activity_type === 'po_archived') {
      const orgName = details.organisation_name as string;
      const fileName = details.file_name as string;
      const purchaseDate = details.purchase_date as string;
      const reason = details.reason as string;
      
      // Format: "Org Name | filename.pdf | Jan 15, 2024: reason"
      const datePart = purchaseDate ? new Date(purchaseDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
      const identifier = [orgName, fileName, datePart].filter(Boolean).join(' | ');
      return reason ? `${identifier}: ${reason}` : identifier || null;
    }
    
    if (log.activity_type === 'po_status_changed') {
      const poNumber = details.po_number as string;
      const oldValue = details.old_value as string;
      const newValue = details.new_value as string;
      return `${poNumber}: ${oldValue} → ${newValue}`;
    }
    
    if (log.activity_type === 'po_line_item_updated' || log.activity_type === 'po_line_item_verified') {
      const poNumber = details.po_number as string;
      const sku = details.sku as string;
      return sku ? `${poNumber} - SKU: ${sku}` : poNumber || null;
    }
    
    return null;
  };

  const activityTypes = [
    'login',
    // 'logout', 
    'data_export',
    'merchant_type_change',
    'po_archived',
    'po_status_changed',
    'po_line_item_updated',
    'po_line_item_verified',
    // 'mfa_setup',
    // 'mfa_verification',
    // 'password_change',
    // 'profile_update'
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">User Activity Logs</h1>
          <p className="text-gray-600">Monitor user activities and system events</p>
        </div>
        <Button onClick={fetchLogs} disabled={loading} className="flex items-center gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Total Activities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <User className="h-4 w-4" />
              Unique Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(logs.map(log => log.user_email)).size}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Last 24h
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {logs.filter(log =>
                new Date(log.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000)
              ).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Download className="h-4 w-4" />
              Data Exports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {logs.filter(log => log.activity_type === 'data_export').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by email..."
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Activity Types</option>
                {activityTypes.map(type => (
                  <option key={type} value={type}>
                    {formatActivityType(type)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setSearchEmail('');
                setFilterType('');
                setCurrentPage(1);
              }}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activity Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>User Email</TableHead>
                  <TableHead>Activity Type</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>User Agent</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
                        Loading activity logs...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No activity logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="font-mono text-sm">
                        {format(new Date(log.created_at), 'MMM d, yyyy HH:mm:ss')}
                      </TableCell>
                      <TableCell>{log.user_email}</TableCell>
                      <TableCell>
                        <Badge className={getActivityBadgeColor(log.activity_type)}>
                          {formatActivityType(log.activity_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs text-sm truncate" title={formatActivityDetails(log) || undefined}>
                        {formatActivityDetails(log) || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {log.ip_address || 'N/A'}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm">
                        {log.user_agent ? (
                          <span title={log.user_agent} className="cursor-help">
                            {log.user_agent.slice(0, 50)}
                            {log.user_agent.length > 50 ? '...' : ''}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} entries
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserActivityLogs;
